import type { DeckIntelligence } from '../types';
import { ViewSectionHeader } from './ViewPage';
import {
  aggregateRisk,
  claimCredibility,
  formatScore,
  marketCrowding,
  overallOpportunityScore,
  scoreBand,
  scoreBandLabel,
  sectionPotential,
  SECTION_SCORE_KEYS,
  synthesisBalance,
  type ScoreDirection,
} from '../utils/deckIntelligenceScores';
import styles from './DeckIntelligenceScoreSummary.module.css';

const SIZE = 320;
const CENTER = SIZE / 2;
const RADIUS = 96;
const LABEL_RADIUS = 124;
const GRID_LEVELS = [3, 5, 7, 9] as const;

type Props = {
  intelligence: DeckIntelligence;
};

function axisAngle(index: number, count: number): number {
  return -Math.PI / 2 + (index * 2 * Math.PI) / count;
}

function polar(angle: number, radius: number): { x: number; y: number } {
  return {
    x: CENTER + radius * Math.cos(angle),
    y: CENTER + radius * Math.sin(angle),
  };
}

function valueRadius(value: number): number {
  return (value / 10) * RADIUS;
}

function ringPath(level: number, count: number): string {
  const r = (level / 10) * RADIUS;
  return Array.from({ length: count }, (_, i) => {
    const { x, y } = polar(axisAngle(i, count), r);
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  })
    .join(' ')
    .concat(' Z');
}

function scorePath(values: number[]): string {
  const count = values.length;
  return values
    .map((value, i) => {
      const { x, y } = polar(axisAngle(i, count), valueRadius(value));
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ')
    .concat(' Z');
}

function labelAnchor(angle: number): 'start' | 'middle' | 'end' {
  const x = Math.cos(angle);
  if (x > 0.25) return 'start';
  if (x < -0.25) return 'end';
  return 'middle';
}

function labelDy(angle: number): number {
  const y = Math.sin(angle);
  if (y < -0.7) return -4;
  if (y > 0.7) return 12;
  return 4;
}

function bandClassName(band: ReturnType<typeof scoreBand>): string {
  if (band === 'strong') return styles.metricBandStrong;
  if (band === 'mixed') return styles.metricBandMixed;
  return styles.metricBandWeak;
}

function MetricCard({
  label,
  value,
  max,
  hint,
  direction = 'higherIsBetter',
}: {
  label: string;
  value: number;
  max: number;
  hint: string;
  direction?: ScoreDirection;
}) {
  const band = scoreBand(value, max, direction);
  const fillPct = max === 0 ? 0 : Math.min(100, (value / max) * 100);
  const fillClass =
    direction === 'higherIsWorse'
      ? `${styles.metricBarFill} ${styles.metricBarFillWorse}`
      : styles.metricBarFill;

  return (
    <article className={styles.metricCard}>
      <div className={styles.metricHeader}>
        <p className={styles.metricLabel}>{label}</p>
        <span className={`${styles.metricBand} ${bandClassName(band)}`}>
          {scoreBandLabel(band, direction)}
        </span>
      </div>
      <p className={styles.metricValue}>{formatScore(value, max)}</p>
      <div className={styles.metricBarTrack} aria-hidden="true">
        <span className={fillClass} style={{ width: `${fillPct}%` }} />
      </div>
      <p className={`${styles.metricHint} muted`}>{hint}</p>
    </article>
  );
}

function PotentialRadar({ intelligence }: Props) {
  const sectionScores = intelligence.sectionScores;
  if (!sectionScores) return null;

  const values = SECTION_SCORE_KEYS.map(({ key }) => sectionScores[key]);
  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  const band = scoreBand(average);

  const summary = SECTION_SCORE_KEYS.map(({ label, key }, i) => `${label} ${values[i]}/10`).join(', ');

  return (
    <figure className={styles.radarFigure}>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className={styles.radarSvg}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Potential profile: ${summary}. Average ${average.toFixed(1)} of 10.`}
      >
        <defs>
          <radialGradient id="deckIntelRadarGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--shamrock-green)" stopOpacity="0.08" />
            <stop offset="100%" stopColor="var(--shamrock-green)" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx={CENTER} cy={CENTER} r={RADIUS + 8} fill="url(#deckIntelRadarGlow)" aria-hidden />

        {GRID_LEVELS.map((level) => (
          <path
            key={level}
            d={ringPath(level, SECTION_SCORE_KEYS.length)}
            className={level === 7 ? styles.radarGridStrong : styles.radarGrid}
            aria-hidden
          />
        ))}

        {SECTION_SCORE_KEYS.map(({ key }, i) => {
          const angle = axisAngle(i, SECTION_SCORE_KEYS.length);
          const outer = polar(angle, RADIUS);
          return (
            <line
              key={key}
              x1={CENTER}
              y1={CENTER}
              x2={outer.x}
              y2={outer.y}
              className={styles.radarAxis}
              aria-hidden
            />
          );
        })}

        <path
          d={scorePath(values)}
          className={band === 'weak' ? styles.radarFillWeak : styles.radarFill}
        />

        {values.map((value, i) => {
          const angle = axisAngle(i, SECTION_SCORE_KEYS.length);
          const point = polar(angle, valueRadius(value));
          return (
            <circle
              key={SECTION_SCORE_KEYS[i].key}
              cx={point.x}
              cy={point.y}
              r={4}
              className={styles.radarDot}
            >
              <title>{`${SECTION_SCORE_KEYS[i].label}: ${value}/10`}</title>
            </circle>
          );
        })}

        {SECTION_SCORE_KEYS.map(({ key, label }, i) => {
          const angle = axisAngle(i, SECTION_SCORE_KEYS.length);
          const labelPoint = polar(angle, LABEL_RADIUS);
          return (
            <text
              key={key}
              x={labelPoint.x}
              y={labelPoint.y}
              dy={labelDy(angle)}
              textAnchor={labelAnchor(angle)}
              className={styles.radarLabel}
            >
              {label}
            </text>
          );
        })}

        <circle cx={CENTER} cy={CENTER} r={24} className={styles.radarCenterBadge} aria-hidden />
        <text x={CENTER} y={CENTER + 1} textAnchor="middle" className={styles.radarCenterValue}>
          {average.toFixed(1)}
        </text>
        <text x={CENTER} y={CENTER + 13} textAnchor="middle" className={styles.radarCenterHint}>
          avg
        </text>
      </svg>
    </figure>
  );
}

function SynthesisBalanceChart({ intelligence }: Props) {
  const { upsideCount, downsideCount, balance } = synthesisBalance(intelligence);
  const total = upsideCount + downsideCount;

  if (total === 0) return null;

  return (
    <figure className={styles.balanceFigure} aria-label={`Synthesis balance: ${upsideCount} upside drivers versus ${downsideCount} death reasons.`}>
      <div className={styles.balanceHeader}>
        <p className={styles.balanceTitle}>Upside vs downside</p>
        <p className={styles.balanceRatio}>{Math.round(balance * 100)}% upside tilt</p>
      </div>
      <div className={styles.balanceBarTrack} aria-hidden="true">
        <span className={styles.balanceBarUpside} style={{ width: `${balance * 100}%` }} />
        <span className={styles.balanceBarDownside} style={{ width: `${(1 - balance) * 100}%` }} />
      </div>
      <div className={styles.balanceLegend}>
        <span>{upsideCount} unicorn drivers</span>
        <span>{downsideCount} death reasons</span>
      </div>
    </figure>
  );
}

export function DeckIntelligenceScoreSummary({ intelligence }: Props) {
  const potential = sectionPotential(intelligence.sectionScores);
  const credibility = claimCredibility(intelligence);
  const risk = aggregateRisk(intelligence);
  const crowding = marketCrowding(intelligence.competitors);
  const overall = overallOpportunityScore(intelligence);
  const balance = synthesisBalance(intelligence);
  const hasBalance = balance.upsideCount + balance.downsideCount > 0;

  const hasAnyScore =
    potential ||
    intelligence.claims.length > 0 ||
    intelligence.risks.length > 0 ||
    crowding.count > 0 ||
    intelligence.sectionScores;

  if (!hasAnyScore) return null;

  const hasOverview = Boolean(intelligence.sectionScores) || overall.score > 0 || hasBalance;

  return (
    <section aria-label="Analysis score summary" className={styles.summary}>
      <ViewSectionHeader
        eyebrow="Scorecard"
        title="At-a-glance scoring"
        subtitle="Potential, credibility, risk, and competitive pressure — computed from the analysis below."
      />

      {hasOverview ? (
        <div className={styles.summaryOverview}>
          {intelligence.sectionScores ? <PotentialRadar intelligence={intelligence} /> : null}

          {overall.score > 0 ? (
            <article className={styles.overallCard}>
              <p className={styles.overallLabel}>Overall opportunity</p>
              <p className={styles.overallValue}>{overall.score.toFixed(1)}/10</p>
              <ul className={styles.overallBreakdown}>
                {overall.components.map((component) => (
                  <li key={component.label}>
                    <span>{component.label}</span>
                    <span>{formatScore(component.value, component.max)}</span>
                  </li>
                ))}
              </ul>
              <SynthesisBalanceChart intelligence={intelligence} />
            </article>
          ) : hasBalance ? (
            <div className={styles.balanceCard}>
              <SynthesisBalanceChart intelligence={intelligence} />
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={styles.summaryMetrics}>
        {potential ? (
          <MetricCard
            label="Potential sum"
            value={potential.total}
            max={potential.max}
            hint={`Average ${potential.average.toFixed(1)}/10 across team, market, product, model, traction, and funding.`}
          />
        ) : null}

        {intelligence.claims.length > 0 ? (
          <MetricCard
            label="Claim credibility"
            value={credibility.average}
            max={10}
            hint={`${Math.round(credibility.bullshitRatio * 100)}% flagged as weak or likely false.`}
          />
        ) : null}

        {intelligence.risks.length > 0 ? (
          <MetricCard
            label="Aggregate risk"
            value={risk.average}
            max={10}
            direction="higherIsWorse"
            hint={`Peak flagged risk ${risk.peak}/10 across ${intelligence.risks.length} items.`}
          />
        ) : null}

        {crowding.count > 0 ? (
          <MetricCard
            label="Market pressure"
            value={crowding.totalStrength}
            max={crowding.maxStrength}
            direction="higherIsWorse"
            hint={`${crowding.count} named competitors · avg strength ${crowding.averageStrength.toFixed(1)}/10.`}
          />
        ) : null}
      </div>
    </section>
  );
}
